import React from 'react';
import Keyboard from 'react-simple-keyboard';
import dayjs from 'dayjs';
import styled, { createGlobalStyle, css } from 'styled-components';
import * as chroma from 'chroma-js';
import { ITheme } from '../../themes';
import { robotoFonts } from '../typography';

const buildLayout = ({ withSubmit, withSymbols = true }: { withSubmit: boolean; withSymbols?: boolean }) => {
  const symbols = withSymbols ? '{symbols} ' : '';

  return {
    default: [
      'q w e r t y u i o p',
      'a s d f g h j k l',
      '{shift} z x c v b n m - {bksp}',
      `${symbols}{space}${withSubmit ? ' {enter}' : ''}`
    ],
    defaultSecondary: [
      'q w e r t y u i o p',
      'a s d f g h j k l',
      '{shift} z x c v b n m @ . {bksp}',
      `{numbersAndReducedSymbols} {space}${withSubmit ? ' {enter}' : ''}`
    ],
    shiftSecondary: [
      'Q W E R T Y U I O P',
      'A S D F G H J K L',
      '{shift} Z X C V B N M , . {bksp}',
      `{numbersAndReducedSymbols} {space}${withSubmit ? ' {enter}' : ''}`
    ],
    email: [
      'q w e r t y u i o p _',
      'a s d f g h j k l @',
      '{shift} z x c v b n m . {bksp}',
      `${symbols}{space}${withSubmit ? ' {enter}' : ''} {dotcom}`
    ],
    shift: [
      'Q W E R T Y U I O P',
      'A S D F G H J K L',
      '{shift} Z X C V B N M - {bksp}',
      `${symbols}{space}${withSubmit ? ' {enter}' : ''}`
    ],
    emailShift: [
      'Q W E R T Y U I O P -',
      'A S D F G H J K L @',
      '{shift} Z X C V B N M . {bksp}',
      `{symbols} {space}${withSubmit ? ' {enter}' : ''} {dotcom}`
    ],
    symbols: [
      '+ _ ~ ! @ # $ % ^ & * ( )',
      '[ ] { } € £ ¥ ; : \' " `',
      '- = | \\ < > … , . ? / {bksp}',
      `{alpha} {space} ${withSubmit ? '{enter}' : ''}`
    ],
    numbersAndReducedSymbols: [
      '1 2 3 4 5 6 7 8 9 0',
      '+ _ # ! @ $ ^ & * ( )',
      '- = \\ … , . ? / ~ {bksp}',
      `{alpha} {space}`
    ],
    defaultSearch: [
      'q w e r t y u i o p',
      'a s d f g h j k l',
      '{shift} z x c v b n m - {bksp}',
      `{symbolsSearch} {space} {hide}`
    ],
    shiftSearch: [
      'Q W E R T Y U I O P',
      'A S D F G H J K L',
      '{shift} Z X C V B N M - {bksp}',
      `{symbolsSearch} {space} {hide}`
    ],
    symbolsSearch: ['1 2 3 4 5 6 7 8 9 0', '_ # ! @ $ & * ( )', `+ ' = \\ , . ? / ~ {bksp}`, `{alpha} {space} {hide}`]
  };
};

const numpad = {
  default: ['7 8 9', '4 5 6', '1 2 3', '. 0 {bksp}']
};

const defaultDisplay = {
  '{bksp}': ' ',
  '{shift}': ' ',
  '{enter}': ' ',
  '{OK}': 'OK',
  '{symbols}': '#+=',
  '{alpha}': 'ABC',
  '{space}': 'SPACE',
  '{email}': '@williamhill.us',
  '{dotcom}': '.com',
  '{numbersAndReducedSymbols}': '123',
  '{symbolsSearch}': '1234',
  '{hide}': ' '
};

function allKeys() {
  const layout = buildLayout({ withSubmit: true });
  return [
    ...layout.default,
    ...layout.shift,
    ...layout.symbols,
    ...numpad.default,
    ...layout.email,
    ...layout.emailShift,
    ...layout.defaultSecondary,
    ...layout.shiftSecondary,
    ...layout.numbersAndReducedSymbols,
    ...layout.defaultSearch,
    ...layout.shiftSearch,
    ...layout.symbolsSearch
  ].join(' ');
}

const qaTaggingButtonTheme = [
  {
    class: 'qa-keypad-input',
    buttons: allKeys()
  },
  {
    class: 'hg-confirm-disabled',
    buttons: '{enter}'
  }
];

interface IStateKeyboard {
  layout:
    | 'default'
    | 'shift'
    | 'symbols'
    | 'email'
    | 'emailShift'
    | 'defaultSecondary'
    | 'shiftSecondary'
    | 'numbersAndReducedSymbols'
    | 'defaultSearch'
    | 'shiftSearch'
    | 'symbolsSearch';
  capsLock: boolean;
}

interface IStateShiftKey {
  lastPress: any;
}

interface IProps {
  value: string;
  className?: string;
  'data-testid'?: string;
  inputRef?: HTMLInputElement | HTMLTextAreaElement | null;
  onChange(value: string): any;
  onSubmit?(): any;
  submitText?: string;
  sizeMultiplier?: number;
  enterDisabled?: boolean;
  emailMode?: boolean;
  strippedKeyboard?: boolean;
  withSymbols?: boolean;
  enhancedSearch?: boolean;
  hideKeyboard?: () => void;
}

export default ({
  value,
  onChange,
  onSubmit,
  className = 'qa-keyboard',
  'data-testid': dataTestid,
  inputRef = null,
  submitText = 'Submit',
  sizeMultiplier = 1,
  enterDisabled = false,
  emailMode = false,
  strippedKeyboard = false,
  withSymbols = true,
  enhancedSearch = false,
  hideKeyboard
}: IProps) => {
  const keyboardLayout = (): [IStateKeyboard['layout'], IStateKeyboard['layout']] => {
    if (enhancedSearch) return ['defaultSearch', 'shiftSearch'];

    if (emailMode) return ['email', 'emailShift'];

    if (strippedKeyboard) return ['defaultSecondary', 'shiftSecondary'];

    return ['default', 'shift'];
  };

  const [defaultKeyboard, shiftKeyboard]: [IStateKeyboard['layout'], IStateKeyboard['layout']] = keyboardLayout();

  const [keyboard, setKeyboard] = React.useState<IStateKeyboard>({
    layout: defaultKeyboard,
    capsLock: false
  });
  const [shiftKey, setShiftKey] = React.useState<IStateShiftKey>({ lastPress: dayjs() });

  const layout = buildLayout({ withSubmit: Boolean(onSubmit), withSymbols });
  const display = { ...defaultDisplay, '{enter}': submitText };

  const handleKeyPress = (e: string) => {
    const range =
      inputRef && typeof inputRef.selectionStart === 'number'
        ? [inputRef.selectionStart, inputRef.selectionEnd || inputRef.selectionStart]
        : null;
    const append = (suffix: string) => {
      if (range) {
        onChange(value.slice(0, range[0]) + suffix + value.slice(range[1]));
        inputRef && inputRef.setSelectionRange(range[0] + suffix.length, range[0] + suffix.length);
      } else {
        onChange(value + suffix);
      }
    };
    const now = dayjs();
    const shiftKeyTimer = now.diff(shiftKey.lastPress);
    const newLayout = keyboard.layout === defaultKeyboard ? shiftKeyboard : defaultKeyboard;
    switch (e) {
      case '{shift}':
        if (keyboard.capsLock) {
          return setKeyboard({ layout: defaultKeyboard, capsLock: false });
        }

        if (shiftKeyTimer < 1000 && keyboard.layout === shiftKeyboard) {
          return setKeyboard({ layout: shiftKeyboard, capsLock: true });
        }

        return setKeyboard({ layout: newLayout, capsLock: false });
      case '{symbols}':
        return setKeyboard({ capsLock: false, layout: 'symbols' });
      case '{numbersAndReducedSymbols}':
        return setKeyboard({ capsLock: false, layout: 'numbersAndReducedSymbols' });
      case '{symbolsSearch}':
        return setKeyboard({ capsLock: false, layout: 'symbolsSearch' });
      case '{alpha}':
        return setKeyboard({ capsLock: false, layout: defaultKeyboard });
      case '{bksp}':
        if (range) {
          if (range[0] === range[1] && range[0] !== 0) {
            onChange(value.slice(0, range[0] - 1) + value.slice(range[0]));
            inputRef && inputRef.setSelectionRange(range[0] - 1, range[0] - 1);
          } else {
            onChange(value.slice(0, range[0]) + value.slice(range[1]));
            inputRef && inputRef.setSelectionRange(range[0], range[0]);
          }
        } else {
          onChange(value.substr(0, value.length - 1));
          inputRef && inputRef.setSelectionRange(value.length - 1, value.length - 1);
        }
        return;
      case '{enter}': {
        if (enterDisabled) return;
        return !enterDisabled && onSubmit && onSubmit();
      }
      case '{email}':
        return append('@williamhill.us');
      case '{dotcom}':
        return append('.com');
      case '{space}':
        return append(' ');
      case '{hide}':
        return hideKeyboard && hideKeyboard();
      default:
        if (!keyboard.capsLock && keyboard.layout === shiftKeyboard) {
          setKeyboard({ layout: defaultKeyboard, capsLock: false });
        }
        return append(e);
    }
  };

  const handleKeyReleased = (e: string) => {
    if (e === '{shift}') {
      setShiftKey({ lastPress: new Date() });
    }
  };

  return (
    <Shelf
      data-testid={dataTestid}
      onMouseDown={e => e.preventDefault()}
      className={`${className} ${keyboard.capsLock && 'caps-lock'}`}
      strippedKeyboard={strippedKeyboard}>
      <GlobalStyle />
      <Splitter strippedKeyboard={strippedKeyboard}>
        <Wrapper
          className="qa-keyboard-wrapper"
          enterDisabled={enterDisabled}
          multiplier={sizeMultiplier}
          strippedKeyboard={strippedKeyboard}>
          <Keyboard
            display={display}
            baseClass="alpha"
            layoutName={keyboard.layout}
            preventMouseDownDefault={true}
            layout={layout}
            onKeyPress={handleKeyPress}
            onKeyReleased={handleKeyReleased}
            buttonTheme={qaTaggingButtonTheme}
            syncInstanceInputs={true}
            disableButtonHold={true}
          />
        </Wrapper>
        {!strippedKeyboard && (
          <Wrapper className="qa-keyboard-wrapper" multiplier={sizeMultiplier} strippedKeyboard={strippedKeyboard}>
            <Keyboard
              display={display}
              baseClass="numpad"
              preventMouseDownDefault={true}
              layout={numpad}
              onKeyPress={handleKeyPress}
              buttonTheme={qaTaggingButtonTheme}
              syncInstanceInputs={true}
            />
          </Wrapper>
        )}
        {enhancedSearch && <BlurredBackground />}
      </Splitter>
    </Shelf>
  );
};

const GlobalStyle = createGlobalStyle<{ theme: ITheme }>`
  .simple-keyboard .hg-row {
    display: flex;
    flex-direction: row;
    margin-bottom: 1rem;
    justify-content: center;
  }
  .simple-keyboard.alpha .hg-row:nth-child(4) {
    margin-right: 6rem;
    margin-left: 5rem;
  }
  .simple-keyboard .hg-button {
    font-stretch: 60%;
    background: ${({ theme }) => theme.components.keyboard.keys};
    margin-left: 1rem;
    font-weight: 300;
    color: ${({ theme }) => theme.colors.white};
    border-radius: 0.5rem;
    font-family: inherit;
    transition: 0.1s;
    user-select: none;
    cursor: pointer;
  }
  .simple-keyboard .hg-button:active {
    transform: scale(1.15);
    transition: 0.1s;
    box-shadow: 0 0 1rem 0 rgba(0,0,0,0.30);
    -webkit-tap-highlight-color: transparent;
  }
  .simple-keyboard .hg-button:first-child {
    margin-left: 0;
  }
  .simple-keyboard .hg-standardBtn,
  .simple-keyboard .hg-button-shift,
  .simple-keyboard .hg-button-enter,
  .simple-keyboard .hg-button-hide,
  .simple-keyboard .hg-button-bksp {
    box-sizing: border-box;
    padding: 0;
    text-align: center;
  }
  .simple-keyboard .hg-button-space {
    padding: 0;
    text-align: center;
    flex: 1;
  }
  .simple-keyboard.hg-layout-default .hg-button-space:active,
  .simple-keyboard.hg-layout-shift .hg-button-space:active {
    transform: scale(1.04);
  }
  .simple-keyboard .hg-button-shift {
    background: ${({ theme }) =>
      theme.components.keyboard.buttons} url(/images/icons/shift.svg) no-repeat center center;
  }
  .simple-keyboard.hg-layout-shift .hg-button-shift {
    background: ${({ theme }) => theme.components.keyboard.buttons}
         url(/images/icons/shift-active.svg) no-repeat center center;
  }
  .caps-lock .simple-keyboard.hg-layout-shift .hg-button-shift,
  .caps-lock .simple-keyboard.hg-layout-emailShift .hg-button-shift,
  .caps-lock .simple-keyboard.hg-layout-shiftSearch .hg-button-shift {
    background: ${({ theme }) =>
      theme.components.keyboard.buttons} url(/images/icons/shift-caps-lock.svg) no-repeat center center;
  }

  .simple-keyboard .hg-button-hide {
    background: ${({ theme }) =>
      theme.components.keyboard.buttons} url(/images/icons/hide-keyboard.svg) no-repeat center center;
  }
  .simple-keyboard .hg-button-enter {
    background: ${({ theme }) => theme.colors.buttons.primary};
    color: ${({ theme }) => theme.colors.buttons.primaryContrast};
  }
  .simple-keyboard .hg-button-bksp {
    background: ${({ theme }) =>
      theme.components.keyboard.buttons} url(/images/icons/delete.svg) no-repeat center center;
    }
  .simple-keyboard .hg-button-numbersAndReducedSymbols,
  .simple-keyboard .hg-button-symbols {
    background: ${({ theme }) => theme.colors.grey500};
    color: ${({ theme }) => theme.colors.white};
  }
  .simple-keyboard .hg-button-symbolsSearch {
    background: ${({ theme }) => theme.components.keyboard.buttons};
    color: #000000;
  }
  .simple-keyboard .hg-button-alpha {
    background: #232424;
    color: ${({ theme }) => theme.colors.white};
    margin-left: 8.25rem;
  }
  .simple-keyboard .hg-button-email {
    flex: 1;
    text-align: center;
    color: ${({ theme }) => theme.colors.grey300};
  }
`;

const Shelf = styled.div<{ strippedKeyboard: boolean }>`
  background: transparent;
  width: 100%;
  max-height: 40rem;
  transition: max-height 0.3s;
  ${({ strippedKeyboard }) => (strippedKeyboard ? 'position: relative;' : 'position: static')};
  display: flex;
  justify-content: center;
  flex-direction: row;
`;

const Splitter = styled.div<{ strippedKeyboard: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: ${({ strippedKeyboard }) => (strippedKeyboard ? '1rem 0 1rem 1rem' : '1rem 0')};
  border-radius: 1.5rem;
`;

const Wrapper = styled.div<{ multiplier: number; strippedKeyboard: boolean; enterDisabled?: boolean }>`
  ${({ strippedKeyboard }) => strippedKeyboard && 'width: 100%;'}
  padding: ${({ strippedKeyboard }) => (strippedKeyboard ? '0' : '0 1.6rem')};
  &:nth-child(2) {
    border-left: 1px solid ${({ theme }) => chroma.hex(theme.colors.brand.primary).alpha(0.3).css()};
  }
  ${({ multiplier }) => css`
    .simple-keyboard .hg-button {
      border-radius: 5rem;
      line-height: calc(7.2rem * ${multiplier});
      height: calc(7.2rem * ${multiplier});
      padding: 0 calc(2.4rem * ${multiplier});
      margin-left: calc(1rem * ${multiplier});
      font-size: ${robotoFonts['3xl']};
    }
    .simple-keyboard .hg-standardBtn,
    .simple-keyboard .hg-button-shift,
    .simple-keyboard .hg-button-enter,
    .simple-keyboard .hg-button-bksp {
      width: calc(7.4rem * ${multiplier});
    }
    .simple-keyboard .hg-button-space {
      width: calc(31.6rem * ${multiplier});
      font-size: calc(3rem * ${multiplier});
    }
    .simple-keyboard .hg-button-enter {
      font-size: calc(3rem * ${multiplier});
      padding: 0 calc(1rem * ${multiplier});
      min-width: calc(14.8rem * ${multiplier});
    }

    .simple-keyboard .hg-button-symbolsSearch,
    .simple-keyboard .hg-button-alpha,
    .simple-keyboard .hg-button-hide {
      width: calc(12.4rem * ${multiplier});
      padding: 0 calc(3.4rem * ${multiplier});
    }
  `};

  ${({ enterDisabled }) =>
    enterDisabled &&
    css`
      .hg-confirm-disabled {
        pointer-events: none;
        opacity: 0.5;
      }
    `}
`;

const BlurredBackground = styled.div`
  background: ${({ theme }) => theme.components.keyboard.background};
  backdrop-filter: blur(5px);
  height: 100%;
  bottom: 0;
  position: absolute;
  width: 100%;
  border-radius: 1rem;
  z-index: -1;
`;
